<script setup lang="ts">
import { createModalAbort } from '@utils/modal-util.js';
import ExpandableImage from '@components/expandable-image.vue';
import { computed, ref } from 'vue';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import IconTooltip from '@components/icons/icon-tooltip.vue';
import { aspectRatioType, sizeType } from '@components/expandable-image-types.ts';

const props = withDefaults(defineProps<{
    basePath?: string|null,
    path: string|null,
    thumbnail?: string|null
    zoom?: boolean,
    disableZoom?: boolean,
    border?: boolean,
    size?: sizeType|false,
    aspectRatio?: aspectRatioType|false,
    enablePointer?: boolean,
    background?: boolean,
    maxHeight?: string,
}>(), {
    basePath: null,
    path: null,
    thumbnail: null,
    zoom: false,
    disableZoom: false,
    border: false,
    size: false,
    aspectRatio: false,
    enablePointer: true,
    background: false,
});

const setPointer = ref<boolean>(props.enablePointer);
if (props.disableZoom) {
    setPointer.value = !props.disableZoom;
}

const ratio = props.aspectRatio !== false ? props.aspectRatio.replace(':', '/') : 'auto';

function zoomImage() {
    if (props.path === null || props.disableZoom) {
        return;
    }
    createModalAbort(
        false,
        ExpandableImage,
        null,
        {
            basePath: props.basePath,
            path: props.path,
            zoom: true,
        },
    );
}

function withBasePath(file: string): string {
    if (!props.basePath) {
        return file;
    }
    return 'https://img.biker-boarder.de' + props.basePath + '/' + file;
}
const previewSrc = computed(() => {
    if (props.path === null) {
        return null;
    }
    const file = props.thumbnail ?? props.path;
    return withBasePath(file);
});
const zoomSrc = computed(() => {
    if (props.path === null) {
        return null;
    }
    return withBasePath(props.path);
});
</script>

<template>
    <div v-if="path !== null">
        <div
            v-if="!zoom"
            :class="[
                $style.preview,
                border ? $style.border : '',
                size ? $style[size] : '',
                setPointer ? $style.set_pointer : '',
                background ? $style.background : '',
                maxHeight ? $style.max_height : '',
            ]"
            @click="zoomImage"
        >
            <img
                :class="$style.image"
                :src="previewSrc ?? ''"
                :alt="path"
            >
        </div>
        <img
            v-else
            :class="[$style.image, $style.zoom_image]"
            :src="zoomSrc ?? ''"
            :alt="path"
        >
    </div>
    <CenterChildrenWrapper
        v-else
        :class="[
            $style.missing_image,
            $style.preview,
            border ? $style.border : '',
            size ? $style[size] : '',
        ]"
    >
        <IconTooltip :class="$style.icon" tooltip="Kein Produktbild" icon="image" icon-color="grey"/>
    </CenterChildrenWrapper>
</template>

<style lang="scss" module>
@use '@borders' as *;

.image {
    width: 100%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
}
.preview {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    &.border {
        padding: 5px;
        border: $thin-line-grey;
    }
    &.background {
        padding: 5px;
        background-color: #fff;
    }
    &.xxs {
        min-width: 2em;
        max-width: 2.5vw;
    }
    &.xs {
        min-width: 4em;
        max-width: 5vw;
    }
    &.s {
        min-width: 6.5em;
        max-width: 10vw;
    }
    &.m {
        min-width: 10em;
        max-width: 18vw;
    }
    &.l {
        min-width: 15em;
        max-width: 30vw;
    }
    &.set_pointer {
        cursor: pointer;
    }
    .image {
        aspect-ratio: v-bind('ratio');
    }
}
.zoom_image {
    max-width: 80vw;
    max-height: 80vh;
}
.missing_image {
    .icon svg {
        width: initial;
        min-height: 1.5em;
    }
}
.max_height {
    height: v-bind('maxHeight');
    position: relative;

    .image {
        max-height: v-bind('maxHeight');
        position: absolute;
        left: 0;
        width: unset;
        max-width: inherit;
    }
}
</style>
